import { useEffect, useState } from "react";
import { useTranslation } from "@chr/online-shared-ui-localizations";
import { Icon, Stack, useTheme, Autocomplete, Box, TextField } from "@mui/material";
import { Banner, Button, MenuItemProps, Typography, UnstableSideSheet } from "@chr/eds-react";
import {
  ErrorCode as DropZoneErrorCode,
  FileError,
  useDropzone,
} from 'react-dropzone';
import { DocumentType, DocumentTypeDisplay } from "@/common/constants/document-types";
import { getDocumentTypeDisplayName } from "@/common/utilities/display-utilities";
import { useUploadDocument } from "@/common/apis/upload-document-mutation";
import { UploadDocumentSuccess } from "./UploadDocumentSuccess";
import { useQueryClient } from "@tanstack/react-query";
import { usePerformanceTimer } from "@/common/hooks/usePerformanceTimer";
import { useAnalytics } from "@/common/hooks/useAnalytics";

export interface UploadDocumentModalProps {
  openModal: boolean;
  handleClose: (event: object) => void;
  shipmentIds: string[];
  bookingNumber: number;
}

interface IDropZoneError {
  message: string;
  isError: boolean;
}
export interface PendingFile {
  documentType: DocumentType | undefined;
  shipmentId: string;
  file: File | null;
}

export const UploadDocumentModal = ({openModal, handleClose, shipmentIds, bookingNumber}: UploadDocumentModalProps) => {
  const theme = useTheme();
  const translate = useTranslation();
  const queryClient = useQueryClient();
  const { trackDocActivity } = useAnalytics();
  const { isLoading, isSuccess, isError, mutate: uploadDocument, reset } = useUploadDocument();
  const [pendingFileData, setPendingFileData] = useState<PendingFile>({ shipmentId: shipmentIds[0] } as PendingFile)
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const [dropZoneError, setDropZoneError] = useState<IDropZoneError>({
    message: '',
    isError: false,
  });

  // Measure the performance of upload activity
  const [elapsedTime] = usePerformanceTimer(isLoading, isSuccess);

  const extensions = [
    "image/bmp",
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/tiff",
    "image/tif",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "text/csv",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ]

  const onClose = (event: object) => {
    handleClose(event);
    resetForm();
  }

  const resetForm = () => {
    setPendingFileData({shipmentId: shipmentIds[0]} as PendingFile);
    setShowErrorBanner(false);
    setDropZoneError({message: '', isError: false});
    reset();
  }

  const fileValidator = (file: File) : FileError | null => {
    if(file.size > 5000000){
      setDropZoneError({message: "File is too large", isError: true});
      return {
        code: DropZoneErrorCode.FileTooLarge,
        message: 'File is too large',
      }
    }

    if(!extensions.includes(file.type)){
      setDropZoneError({message: "File type is not valid", isError: true});
      return {
        code: DropZoneErrorCode.FileInvalidType,
        message: 'File type is not valid',
      }
    }

    if (file.name.length > 255) {
      setDropZoneError({message: "File name is too long", isError: true});
      return {
        code: 'file-name-too-long',
        message: 'File name is too long',
      }
    }
    setPendingFileData({...pendingFileData, file: file});
    return null;
  }

  const { getRootProps, getInputProps, open } = useDropzone({
    accept: {
      'image/bmp': ['.bmp'],
      'image/jpeg': ['.jpeg'],
      'image/jpg': ['.jpg'],
      'image/png': ['.png'],
      'image/tiff': ['.tiff'],
      'image/tif': ['.tif'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    noDrag: true,
    maxFiles: 1,
    maxSize: 5000000,
    validator: fileValidator,
    noClick: true,
  });

  const getDocumentTypeMenuItems = () => {
    const menuItems: MenuItemProps[] = [];
    const documentTypes = Object.keys(DocumentTypeDisplay);
    documentTypes.forEach((docType) => {
      if(Number(docType) >= 0){
        menuItems.push({
          value: docType,
          label: translate(getDocumentTypeDisplayName(docType as unknown as DocumentType))
        })
      }
    });
    return menuItems;
  }

  const handleDocTypeOnChange = (value: MenuItemProps | null, reason: string) => {
    if (value) {
      setPendingFileData({...pendingFileData, documentType: value.value as DocumentType});
    } else if (reason === 'clear') {
      setPendingFileData({...pendingFileData, documentType: undefined});
    }
  }

  const truncateFileName = (fileName: string, maxLength: number) => {
    if (fileName.length > maxLength) {
      return fileName.substring(0, maxLength) + '...';
    } else {
      return fileName;
    }
  }

  const handleUpload = () => {
    uploadDocument({
      bookingNumber,
      document: pendingFileData.file!, // eslint-disable-line @typescript-eslint/no-non-null-assertion
      documentType: pendingFileData.documentType!, // eslint-disable-line @typescript-eslint/no-non-null-assertion
      shipmentId: pendingFileData.shipmentId
    });
  }

  useEffect(() => {
    if(isError){
      setShowErrorBanner(true);
    }
    if(isSuccess){
      queryClient.invalidateQueries(['documents', bookingNumber]);
    }
  }, [isError, setShowErrorBanner, isSuccess, queryClient, bookingNumber])

  useEffect(() => {
    isSuccess && trackDocActivity({
      numberOfDocuments: 1,
      typeOfDocument: pendingFileData.documentType ? getDocumentTypeDisplayName(pendingFileData.documentType) : '',
      buttonText: 'Upload',
      activityResult: 'Upload',
      activityResponseTime: elapsedTime,
    });
  }, [elapsedTime]);

  if (isSuccess) {
    return (
      <UnstableSideSheet
        open={openModal}
        onClose={onClose}
        title={translate('Add Document')}
        content={<UploadDocumentSuccess {...{pendingFileData, onAgain: resetForm}} />}
        primaryAction={{
          buttonText: translate('Close'),
          onClick: onClose,
        }}
      />
    );
  }

  return (
    <UnstableSideSheet
      open={openModal}
      onClose={onClose}
      title={translate('Add Document')}
      content={
        <Stack spacing={4}>
          <Stack mt={4} spacing={4}>
          {showErrorBanner &&
            <Banner
              onClose={()=>{setShowErrorBanner(false)}}
              title={translate('File could not be added.')}
              description={translate('Please try again later.')}
              severity="negative"
            />
          }
          <Typography variant="body100">
            {translate('Attach a document to this booking.')}
          </Typography>
          <Stack spacing={1}>
            <Typography variant="subtitle200">
              {translate('Select File')}
            </Typography>
            <Typography variant="body100">
              {translate('Supported file types: .bmp, .jpg, .png, .tif, .pdf, .doc, .docx, .ppt, .pptx, .csv, .xls, .xlsx')}
            </Typography>
            <Typography variant="body100">
              {translate('5MB limit per file.')}
            </Typography>
          </Stack>
          {!pendingFileData.file ? (
            <Stack
              id='file-upload-area'
              {...getRootProps()}
              width='fit-content'
              sx={{
                marginTop: '0px !important'
              }}
            >
              <input data-testid='file-input' {...getInputProps()}/>
              <Stack direction="row" alignItems='center'>
              <Button
                variant="tertiary"
                aria-label='choose file'
                onClick={open}
                buttonText={translate("Choose File")}
              />
            {dropZoneError.isError && dropZoneError.message && (
              <>
                <Icon sx={{marginLeft: '6px'}} fontSize="small" color='error'>error_outline</Icon>
                &nbsp;
                <Typography variant="body200" color={theme.palette.error.main}>
                  {translate(dropZoneError.message)}
                </Typography>
              </>
            )}
              </Stack>
            </Stack>
            ) : (
              <Stack direction='row' alignItems='center' spacing={1}
                sx={{
                  marginTop: '0px !important',
                  '.MuiTypography-root': {
                    wordBreak: 'break-all',
                  },
                  '.MuiButtonBase-root': {
                    padding: '8px 24px',
                  },
                }}
              >
                <Icon fontSize="large">attach_file</Icon>
                <Typography variant="subtitle100" >
                  {truncateFileName(pendingFileData.file.name, 45)}
                </Typography>
                <Button
                  variant="tertiary"
                  aria-label='remove file'
                  onClick={() => setPendingFileData({...pendingFileData, file: null})}
                  buttonText={translate("Remove File")}
                />
              </Stack>
            )
          }
          {/* Implement EDS SelectV2 when available */}
          <Autocomplete
            id='document-type-select'
            data-testid='document-type-autocomplete'
            fullWidth={true}
            options={getDocumentTypeMenuItems()}
            onChange={(_, value, reason) => handleDocTypeOnChange(value, reason)}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value &&
              option.label === value.label
            }
            renderOption={(props, option) => {
              return (
                <Box component='li' {...props}>
                  {option.label}
                </Box>
              )
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate('Document Type *')}
                sx={{
                  "& .MuiAutocomplete-endAdornment .MuiSvgIcon-root": {
                    width: 24,
                    height: 24,
                  },
                }}
              />
            )}
            />
          <Stack>
            <Typography variant="subtitle200">{translate("Shipment Number")}</Typography>
            <Typography variant="body100">{shipmentIds[0]}</Typography>
          </Stack>
        </Stack>

        <Stack 
          direction="row" 
          spacing={2} 
          justifyContent="space-between"
          sx={{
            '& .MuiButton-root': {
              textTransform: 'none',
            }
          }}
        >
          <Button
            data-testid="upload-document-button"
            variant="primary"
            buttonText={translate('Upload')}
            loading={isLoading}
            disabled={!pendingFileData.file || !pendingFileData.shipmentId || !pendingFileData.documentType || isLoading}
            onClick={handleUpload}
            fullWidth
          />
          <Button
            data-testid="cancel-upload-button"
            variant="secondary"
            buttonText={translate('Cancel')}
            disabled={isLoading}
            onClick={onClose}
            fullWidth
          />
        </Stack>
      </Stack>
      }
    />
  );
}
